import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "./Button";
import { cn } from "@/lib/utils";

interface HeroSectionProps {
  backgroundImage: string;
  title: string;
  subtitle?: string;
  ctaPrimary?: { text: string; link: string };
  ctaSecondary?: { text: string; link: string };
  height?: "full" | "large" | "medium";
  overlay?: boolean;
  centered?: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  backgroundImage,
  title,
  subtitle,
  ctaPrimary,
  ctaSecondary,
  height = "full",
  overlay = true,
  centered = true,
}) => {
  const heightClasses = {
    full: "min-h-screen",
    large: "min-h-[70vh]",
    medium: "min-h-[55vh]",
  };

  return (
    <section className={cn("relative flex items-center justify-center overflow-hidden", heightClasses[height])}>
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        {overlay && <div className="absolute inset-0 bg-hero-overlay" />}
      </div>

      {/* Content */}
      <div className={cn("relative z-10 container-custom py-20", centered && "text-center")}>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-primary-foreground mb-6"
        >
          {title}
        </motion.h1>

        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-xl md:text-2xl text-primary-foreground/90 font-body max-w-3xl mx-auto mb-10"
          >
            {subtitle}
          </motion.p>
        )}

        {(ctaPrimary || ctaSecondary) && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            {ctaPrimary && (
              <Button href={ctaPrimary.link} variant="secondary" size="lg">
                {ctaPrimary.text}
              </Button>
            )}
            {ctaSecondary && (
              <Button href={ctaSecondary.link} variant="outlineLight" size="lg">
                {ctaSecondary.text}
              </Button>
            )}
          </motion.div>
        )}
      </div>

      {/* Scroll Indicator */}
      {height === "full" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-primary-foreground/50 rounded-full flex justify-center pt-2"
          >
            <motion.div className="w-1.5 h-3 bg-primary-foreground/70 rounded-full" />
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default HeroSection;
